import { useCallback, useEffect, useRef, useState } from 'react';
import type { FxKind, ProjKind } from '@sd/shared';

export interface FxEvent {
  id: number;
  kind: FxKind;
  targetId: string;
  /** Для arc fx — id атакуючого */
  fromId?: string;
  /** Тип projectile для arc */
  projKind?: ProjKind;
  value?: number;
  ttl?: number;
}

let _seq = 0;

/**
 * Черга FX-анімацій. Кожна анімація живе TTL мс, потім зникає.
 */
export function useFxQueue(): {
  fxs: FxEvent[];
  push: (kind: FxKind, targetId: string, opts?: { value?: number; ttl?: number; fromId?: string; projKind?: ProjKind }) => void;
  forTarget: (targetId: string) => FxEvent[];
} {
  const [fxs, setFxs] = useState<FxEvent[]>([]);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const push = useCallback((kind: FxKind, targetId: string, opts?: { value?: number; ttl?: number; fromId?: string; projKind?: ProjKind }) => {
    const id = ++_seq;
    const ttl = opts?.ttl ?? defaultTtl(kind);
    setFxs((prev) => [...prev, { id, kind, targetId, value: opts?.value, ttl, fromId: opts?.fromId, projKind: opts?.projKind }]);
    timers.current[id] = setTimeout(() => {
      setFxs((prev) => prev.filter((f) => f.id !== id));
      delete timers.current[id];
    }, ttl);
  }, []);

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  const forTarget = useCallback((targetId: string) => fxs.filter((f) => f.targetId === targetId), [fxs]);

  return { fxs, push, forTarget };
}

function defaultTtl(kind: FxKind): number {
  switch (kind) {
    case 'hit': return 500;
    case 'crit': return 600;
    case 'heal': return 1200;
    case 'cast': return 900;
    case 'death': return 1400;
    case 'miss': return 1200;
    case 'status': return 1400;
    case 'arc': return 1000;
  }
}
